import type { Brand } from '@/domainLogic/index.ts';
import { useCommonClient } from '@/app/common/commonClientProvider';
import { useCallback, useEffect, useState } from 'react';
import { URLParameters } from '@/app/common/urlParameters';
import { format, parse } from 'date-fns';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { useSelectedMealPlan } from '@/app/components/hotel-detail/SelectedMealPlanProvider';
import { BottomContinuePanel } from '@/app/components/hotel-detail/BottomContinuePanel';
import { EvoRoomPanelModalContentMobile } from '@/app/components/hotel-detail/evo/rooms/EvoRoomPanelModalContentMobile';
import { EvoRoomPanelModalContentDesktop } from '@/app/components/hotel-detail/evo/rooms/EvoRoomPanelModalContentDesktop';
import { createHotelRoomOptionClickedPayload } from '@/app/common/starTrack/events/hotelRoomOptionClicked.ts';
import OpenXModal from '../../search/components/Modal/OpenXModal';
import { useTranslation } from '../../translations/Translation';
import BasicModalHeader from '../../search/components/Modal/LayoutElements/BasicModalHeader';
import ModalHeader from '../../search/components/Modal/LayoutElements/ModalHeader';
import ModalStickyFooter from '../../search/components/Modal/LayoutElements/ModalStickyFooter';
import { RoomPanelModalContentMobile } from './RoomPanelModalContentMobile';
import { useRoomPanelModal } from './RoomPanelModalProvider';
import { useIsDesktopOrGreater } from '../hooks/useMediaSSR';
import { useIsModalViewport, useSsrMedia } from '../../search/utils/useSsrMedia';
import { RoomPanelModalContent } from './RoomPanelModalContent';
import { useUrlSearchParams } from '../UrlSearchParamsProvider';
import { useInitialParams } from '../InitialParamsProvider';
import { findMealPlan, getMealPlanCode, hasFlashSalePromotion, shouldShowStrikeThroughPrice } from './roomCommon';
import getSearchParams from '../../search/common/getSearchParams';
import { useCuriosity } from '../CuriosityProvider';
import { useOffersContext } from '../OffersProvider';
import { getCancellationPolicy, getRoomCancellationOptions } from './RoomPanelCommon';
import { useHotelPromotion } from '../hooks/useFetchHotelPromotions';
import { useTrackEvoRoomSelection } from '../evo/hooks/useTrackEvoRoomSelection';
import { useEvoGoToNextStepFromRooms } from '../evo/hooks/useEvoContinueFromRooms';

export default function RoomPanelModal() {
  const { isOpen, selectedRoom, preselectedMealPlan, closeModal } = useRoomPanelModal();
  const { t } = useTranslation();
  const urlSearchParams = useUrlSearchParams();
  const { isTrain, children: numberOfChildren, infants, nights } = urlSearchParams;
  const initialParams = useInitialParams();
  const { localization, commonData } = usePageWrapperData();
  const { handleRoomChosen, starTrack } = useCommonClient();
  const isDesktop = useIsDesktopOrGreater();
  const { sendCuriosityRoomRedirectEvent } = useCuriosity();
  const { offersData } = useOffersContext();
  const { setSelectedRoom: setContextSelectedRoom, setSelectedMealPlan: setContextSelectedMealPlan } =
    useSelectedMealPlan();
  const { showLmnDealsPromotion } = useHotelPromotion();

  const searchParams = getSearchParams();
  const searchId = searchParams[URLParameters.SEARCH_ID];
  const { trackRoomSelection } = useTrackEvoRoomSelection();
  const goToNextEvoStep = useEvoGoToNextStepFromRooms();

  const extendedRoomPanel = localization?.configValuesApp?.mobileShowAllRooms || false;
  const evolutionEnabled = localization?.configValuesApp?.evolutionEnabled || false;
  // The design system turns the sheet into a centred dialog at MODAL_DESKTOP_BREAKPOINT_PX, so the swipe-to-dismiss
  // gesture follows that same width for EVO and legacy alike: dragging a centred dialog overwrote its centring
  // transform (MPH-748). EVO also picks its body and footer CTA width there, since a tablet already shows the
  // dialog chrome and used to fill it with the full-bleed mobile body until xl — unless the viewport is too short
  // for the dialog body (a landscape phone: 64px header + 304px gallery + footer in a dialog capped at the viewport
  // minus 64px), which keeps the compact mobile body. Legacy keeps its xl gate for body and class names.
  const isSheet = useIsModalViewport(commonData.isMobileDevice);
  const isShortViewport = useSsrMedia('(max-height: 500px)');
  const isEvoDesktop = evolutionEnabled && !isSheet && !isShortViewport;
  const isDesktopLayout = evolutionEnabled ? isEvoDesktop : isDesktop;
  // EVO shows the continue CTA in the footer on both mobile and desktop; the legacy flow keeps it mobile-only.
  const showFooter = evolutionEnabled || (!isDesktop && !extendedRoomPanel);

  // Prefer the meal plan the opener was displaying (e.g. the board class selected in the rooms list) so the
  // modal's cancellation options match what the user was looking at; initialParams only holds the page-load
  // default, which would otherwise show a different board class's options.
  const resolveSelectedMealPlan = useCallback(
    (room: RoomType | undefined) => {
      if (!room) return undefined;
      if (preselectedMealPlan && room.configurations.some((config) => config.id === preselectedMealPlan.id)) {
        return preselectedMealPlan;
      }
      return initialParams.selectedMealPlanId || initialParams.selectedMealPlanCode
        ? (findMealPlan(room.configurations, initialParams.selectedMealPlanId, initialParams.selectedMealPlanCode) ??
            room.configurations[0])
        : (room.configurations[room.selectedMealPlanIndex] ?? room.configurations[0]);
    },
    [preselectedMealPlan, initialParams.selectedMealPlanId, initialParams.selectedMealPlanCode]
  );

  const [selectedMealPlan, setSelectedMealPlan] = useState<MealPlanType | undefined>(() =>
    resolveSelectedMealPlan(selectedRoom)
  );

  const showLmnDeal = showLmnDealsPromotion && hasFlashSalePromotion(selectedMealPlan);

  useEffect(() => {
    if (selectedRoom) {
      setSelectedMealPlan(resolveSelectedMealPlan(selectedRoom));
    }
  }, [selectedRoom, resolveSelectedMealPlan]);

  if (!selectedRoom) return null;

  const onSelectMealPlan = (mealPlan: MealPlanType) => {
    setSelectedMealPlan(mealPlan);
  };

  const onContinueClick = (mealPlan: MealPlanType) => {
    if (evolutionEnabled) {
      // DP evolution: the room information modal's Continue commits the room/option, so it fires both
      // HOTEL_ROOM_CLICKED and HOTEL_ROOM_OPTION_CLICKED. The legacy flow tracks the click on room open
      // instead, so it keeps sending only HOTEL_ROOM_OPTION_CLICKED here.
      trackRoomSelection(selectedRoom, mealPlan);
    } else {
      starTrack.hotelRoomOptionClicked(
        createHotelRoomOptionClickedPayload({
          searchId: searchId || '',
          initialParams,
          room: selectedRoom,
          mealPlan,
        })
      );
    }

    const groupedCancellationPolicies = getRoomCancellationOptions(selectedRoom.configurations || []);
    const cancellationKey = getCancellationPolicy(mealPlan?.cancellationPolicy || { type: 'UNKNOWN' });
    const mealPlansPerCancellationPolicy =
      groupedCancellationPolicies[cancellationKey].deferredPaymentDepositPerMealPlan.length;
    sendCuriosityRoomRedirectEvent({
      people_child: numberOfChildren,
      people_infant: infants,
      checkin_date: offersData?.checkin ? format(parse(offersData.checkin, 'yyyyMMdd', new Date()), 'yyyy-MM-dd') : '', // Format YYYY-MM-DD
      checkout_date: offersData?.checkout
        ? format(parse(offersData.checkout, 'yyyyMMdd', new Date()), 'yyyy-MM-dd')
        : '', // Format YYYY-MM-DD
      nights: offersData?.nights || nights,
      product_type: isTrain ? 'TH' : 'DP',
      cheapest_price: offersData?.cheapestFilteredMealPlan?.finalTotalPrice,
      cheapest_price_currency: localization.currency,
      room_id: selectedRoom.roomId,
      room_has_photo: (selectedRoom.images.length || 0) > 0 ? 1 : 0,
      room_options: `${Object.keys(groupedCancellationPolicies).length},${mealPlansPerCancellationPolicy}`,
      room_position:
        offersData?.rooms?.findIndex((currentRoom) => currentRoom.roomOfferId === selectedRoom.roomOfferId) || 0,
      is_cheapest_room: offersData?.rooms?.[0]?.roomOfferId === selectedRoom.roomOfferId ? 1 : 0,
      chosen_room_id: selectedRoom.roomId,
      chosen_meal_plan_id: mealPlan.id,
      chosen_meal_plan_name: mealPlan.mealPlanName,
      chosen_cancellation_name: mealPlan.cancellationPolicy.type.toLowerCase().replace('_', ' '),
      cheapest_meal_plan_id: offersData?.cheapestFilteredMealPlan?.id,
      cheapest_meal_plan_name: offersData?.cheapestFilteredMealPlan?.mealPlanName,
      provider_id: mealPlan.supplier.id,
    });

    setContextSelectedRoom(selectedRoom);
    setContextSelectedMealPlan(mealPlan);

    if (evolutionEnabled) {
      // The next step is the flight page on a package and checkout on Hotel Only, which has no flights
      // to choose. The context setters above have not landed yet, so pass this modal's own selection
      // rather than letting the handler read the provider (MPH-935).
      goToNextEvoStep(selectedRoom, mealPlan);
      closeModal();
    } else {
      handleRoomChosen(
        selectedRoom.roomOfferId,
        mealPlan.id,
        getMealPlanCode(mealPlan),
        offersData?.transport?.transports?.[0].id ?? ''
      );
    }

    // Needed for IOS v18 viewport fix
    // TODO: check if still needed
    // resetScrollBlock();
  };

  return (
    <OpenXModal
      isOpen={isOpen || false}
      isMobile={isSheet}
      onClose={closeModal}
      fullScreen={false}
      brand={localization.brand as Brand}
      className={isDesktopLayout ? 'room-details-modal-desktop' : 'mobile-modal-full-height '}
      disableBrowserHistoryNavigation={true}
      id="room-details-modal"
      aria-label={t('vacation.select.room_modal.title')}
      bodyBackgroundColor="backgroundPage"
      scrollShadows={evolutionEnabled}
      header={{
        sticky: true,
        element: <ModalHeader handleClose={closeModal}>{t('vacation.select.room_modal.title')}</ModalHeader>,
      }}
      {...(showFooter
        ? {
            footer: {
              sticky: true,
              element: selectedMealPlan && (
                <ModalStickyFooter>
                  <BottomContinuePanel
                    selectedMealPlan={selectedMealPlan}
                    showPrice={!!selectedMealPlan}
                    onContinueClick={() => onContinueClick(selectedMealPlan)}
                    // EVO uses the plain primary CTA on both mobile and desktop — no lastminute-deal gradient on the
                    // price/button; only the legacy flow keeps the deal styling.
                    isLmnDeal={evolutionEnabled ? false : showLmnDeal}
                    showStrikeThroughPrice={shouldShowStrikeThroughPrice(
                      selectedMealPlan,
                      urlSearchParams.accommodationOnly
                    )}
                    // Figma 979-40680: on EVO this CTA is the 48px-tall (large) button at every width, matching
                    // the room cards behind it — MPH-846 caught it rendering the 40px one below xl. Legacy keeps
                    // the DS default.
                    buttonSize={evolutionEnabled ? 'large' : 'regular'}
                    // The 40px horizontal padding rides on the EVO desktop body (isEvoDesktop above), so the CTA and
                    // EvoRoomPanelModalContentDesktop flip together.
                    wideButton={isEvoDesktop}
                  />
                </ModalStickyFooter>
              ),
            },
          }
        : undefined)}
    >
      {isEvoDesktop ? (
        <EvoRoomPanelModalContentDesktop
          room={selectedRoom}
          selectedMealPlan={selectedMealPlan}
          onSelectMealPlan={onSelectMealPlan}
        />
      ) : isDesktop ? (
        <RoomPanelModalContent room={selectedRoom} />
      ) : evolutionEnabled ? (
        <EvoRoomPanelModalContentMobile
          room={selectedRoom}
          selectedMealPlan={selectedMealPlan}
          onSelectMealPlan={onSelectMealPlan}
        />
      ) : (
        <RoomPanelModalContentMobile
          room={selectedRoom}
          selectedMealPlan={selectedMealPlan}
          onContinueClick={onContinueClick}
          onSelectMealPlan={onSelectMealPlan}
        />
      )}
    </OpenXModal>
  );
}

type RoomDetailsModalProps = {
  isOpen: boolean;
  onCloseClick: () => void;
  onOpenByBrowserHistory?: () => void;
  selectedRoom: RoomType | undefined;
};

export function RoomDetailsModal(props: RoomDetailsModalProps) {
  const { isOpen, selectedRoom, onCloseClick, onOpenByBrowserHistory } = props;
  const { t } = useTranslation();
  const { localization, commonData } = usePageWrapperData();

  const isDesktop = useIsDesktopOrGreater();
  // Same split as RoomPanelModal: the gesture follows the design system's sheet/dialog switch, the body its xl gate.
  const isSheet = useIsModalViewport(commonData.isMobileDevice);

  if (!selectedRoom) return null;

  return (
    <OpenXModal
      isOpen={isOpen}
      isMobile={isSheet}
      onClose={onCloseClick}
      onCloseByBrowserHistory={onCloseClick}
      onOpenByBrowserHistory={onOpenByBrowserHistory}
      brand={localization.brand as Brand}
      className={isDesktop ? 'room-details-modal-desktop' : 'room-details-modal'}
      id="selected-room-details"
      aria-label={t('vacation.select.details.rooms.allinfo')}
      bodyBackgroundColor="backgroundPage"
      header={{
        sticky: true,
        element: (
          <BasicModalHeader handleClose={onCloseClick}>{t('vacation.select.details.rooms.allinfo')}</BasicModalHeader>
        ),
      }}
    >
      {isDesktop ? (
        <RoomPanelModalContent room={selectedRoom} />
      ) : (
        <RoomPanelModalContentMobile room={selectedRoom} hideMealPlanOptionsAdditionalInformation={true} />
      )}
    </OpenXModal>
  );
}
